/**
 * Navigation slice for managing navigation items state
 * Handles the visibility/toggle state of content items in EntityPage
 */
import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  // Items states for the current entity (indicatorKey -> boolean)
  itemsStates: {},
  // Indicators status for the current entity (indicatorKey -> boolean)
  indicatorsWithStatus: {},
  // Current entity ID
  currentEntityId: null,
  // Current entity data (with domaine, etc.)
  currentEntity: null,
};

const navigationSlice = createSlice({
  name: 'navigation',
  initialState,
  reducers: {
    /**
     * Set the complete items states object (usually from indicatorsWithStatus)
     * @param {Object} state - Current state
     * @param {Object} action - Redux action
     * @param {Object} action.payload - Object with itemsStates, indicatorsWithStatus, entityId, entity
     */
    setItemsStates: (state, action) => {
      const { itemsStates, indicatorsWithStatus, entityId, entity } = action.payload;
      state.itemsStates = itemsStates;
      state.indicatorsWithStatus = indicatorsWithStatus;
      state.currentEntityId = entityId;
      state.currentEntity = entity || null;
    },

    /**
     * Toggle a specific navigation item
     * @param {Object} state - Current state
     * @param {Object} action - Redux action
     * @param {string} action.payload - Item identifier to toggle
     */
    toggleItem: (state, action) => {
      const itemKey = action.payload;
      state.itemsStates[itemKey] = !state.itemsStates[itemKey];
    },

    /**
     * Set a specific item state
     * @param {Object} state - Current state
     * @param {Object} action - Redux action
     * @param {Object} action.payload - Object with itemKey and value
     */
    setItemState: (state, action) => {
      const { itemKey, value } = action.payload;
      state.itemsStates[itemKey] = value;
    },

    /**
     * Reset all states (when navigating away or unmounting)
     * @param {Object} state - Current state
     */
    resetNavigationState: (state) => {
      state.itemsStates = {};
      state.indicatorsWithStatus = {};
      state.currentEntityId = null;
      state.currentEntity = null;
    },
  },
});

export const {
  setItemsStates,
  toggleItem,
  setItemState,
  resetNavigationState,
} = navigationSlice.actions;

export default navigationSlice.reducer;

// Selectors
export const selectItemsStates = (state) => state.navigation.itemsStates;
export const selectIndicatorsWithStatus = (state) => state.navigation.indicatorsWithStatus;
export const selectCurrentEntityId = (state) => state.navigation.currentEntityId;
export const selectCurrentEntity = (state) => state.navigation.currentEntity;
export const selectItemState = (itemKey) => (state) => state.navigation.itemsStates[itemKey];